import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { Button } from '@ast/magma/components/button';
import { DropDownList } from '@ast/magma/components/dropdownlist';

import { FormControl, formControlRequiredRule } from '@app/common/components/controls';
import { FlexContainer } from '@app/common/components/FlexContainer/FlexContainer';
import { useNotifierContext } from '@app/common/components/Notifier';
import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { useUserTimeZone } from '@app/widgets/contacts-and-settings/hooks/useUserTimeZone';
import { useSetTimeZoneMutation } from '@app/widgets/contacts-and-settings/queries/queryTyping/setTimeZone';
import { mapErrorsToAlerts, ServerErrors } from '@app/widgets/contacts-and-settings/utils/errorsMapper';

import styles from './TimeZone.pcss';
import { TimeZoneForm, TimeZoneFormFields } from './types';

export type TimeZoneProps = {
  backRoute: string
};

export const TimeZone = ({ backRoute }: TimeZoneProps) => {
  const { t } = useUserSettingsTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const { userTimeZone, timeZones, loading: fetchLoading } = useUserTimeZone();
  const [setTimeZone, { error, loading: mutationLoading }] = useSetTimeZoneMutation();
  const { addAlerts, clearAlerts } = useNotifierContext();

  const form = useForm<TimeZoneForm>({
    mode: 'onSubmit',
  });

  const validationRules = useMemo(() => [
    formControlRequiredRule(t(
      'profile-and-security.user-profile.time-zone.validation.required|Message for time zone required validation rule on edit time zone screen',
      'This field is required',
    )),
  ], []);

  useEffect(() => {
    if (userTimeZone) {
      // set initial value when data is loaded
      form.reset({ timeZone: userTimeZone });
    }
  }, [userTimeZone]);

  const handleSubmit = async ({ timeZone }: TimeZoneForm) => {
    const { data } = await setTimeZone({
      variables: {
        data: {
          id: timeZone.id,
          name: timeZone.name,
        },
      },
    });

    clearAlerts();
    addAlerts(
      data?.timeZone.errors?.length
        ? mapErrorsToAlerts(data.timeZone.errors as ServerErrors)
        : [{
          level: 'success',
          title: t(
            'profile-and-security.user-profile.time-zone.alert.success|Successfull time zone change message text',
            'Time zone successfully changed',
          ),
        }],
    );
  };

  if (error) {
    throw error;
  }

  return (
    <FormProvider {...form}>
      <form
        className={styles.form}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <OverlaySpinner visible={fetchLoading || mutationLoading} />
        <FlexContainer direction="column" gap="extraLarge">
          <FormControl
            className={styles.formControl}
            name={TimeZoneFormFields.TIME_ZONE}
            validations={validationRules}
          >
            <DropDownList
              label={t(
                'profile-and-security.user-profile.time-zone.dropdown.label|Time zone selector label',
                'Set your time zone',
              )}
              data-stable-name="TimeZoneSelector"
              className={styles.timeZoneSelector}
              data={timeZones || []}
              getItemLabel={(item) => item?.name}
            />
          </FormControl>
          <FlexContainer
            direction={isMobile ? 'column-reverse' : 'row'}
            gap="small"
          >
            <Button
              type="button"
              look="outlined"
              predefinedSize="medium"
              data-stable-name="CancelButton"
              onClick={() => history.push(backRoute)}
            >
              {t(
                'profile-and-security.edit-time-zone.cancel-button.text|Cancel time zone editing button',
                'Cancel',
              )}
            </Button>
            <Button
              type="submit"
              look="primary"
              predefinedSize="medium"
              data-stable-name="SaveButton"
            >
              {t(
                'profile-and-security.edit-time-zone.save-button.text|Save time zone button',
                'Save',
              )}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </form>
    </FormProvider>
  );
};
